import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ensureUids } from '../../utils/outlineUtil';
import { useWizardStore, OutlineItem } from './useWizardStore';
import WizardLayout from './WizardLayout';
import PageHeader from '../layout/PageHeader';
import api from '../../services/api';
import { toast, confirm } from '../../store/toastStore';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  editItem as utilEdit,
  deleteItem as utilDelete,
  addRootItem,
  addChildItem,
  addSiblingItem,
  insertSiblingItem,
  moveItem,
} from '../../utils/outlineUtil';

const CHAPTER_TAGS = [
  '默认', '技术方案', '实施方案', '项目管理',
  '安全方案', '运维方案', '培训方案', '售后服务',
  '公司介绍', '团队介绍', '业绩案例', '需求分析',
];

const StepOutline: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const project = useWizardStore((s) => s.project);
  const setCurrentStep = useWizardStore((s) => s.setCurrentStep);

  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const [outlineLoaded, setOutlineLoaded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!id || outlineLoaded) return;
    api.get(`/api/projects/${id}`).then((res) => {
      const raw = res.data.outline_data?.outline || [];
      setOutline(ensureUids(raw));
      setOutlineLoaded(true);
    }).catch(() => {});
  }, [id, outlineLoaded]);

  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTag, setEditTag] = useState('默认');

  const toggleExpand = (uid: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  // ── 编辑 ──
  const startEditingOutline = (item: OutlineItem) => {
    setEditingItem(item.uid);
    setEditTitle(item.title);
    setEditDescription(item.description || '');
    setEditTag(item.tag || '默认');
  };

  const cancelEditingOutline = () => {
    setEditingItem(null);
    setEditTitle('');
    setEditDescription('');
    setEditTag('默认');
  };

  const saveEditOutline = async () => {
    if (!editingItem) return;
    const updates: any = { title: editTitle.trim(), description: editDescription.trim() };
    if (editTag && editTag !== '默认') updates.tag = editTag;
    const newOutline = utilEdit(outline, editingItem, updates);
    setOutline(newOutline);
    cancelEditingOutline();
    // Auto-save to backend so changes survive refresh
    try {
      await api.put(`/api/projects/${id}`, { outline_data: { outline: newOutline } });
    } catch {}
  };

  // ── 删除 ──
  const deleteOutlineItem = async (itemId: string) => {
    if (!await confirm({ title: '确认删除', message: '确定要删除这个目录项吗？子目录也会一并删除。', confirmLabel: '删除', danger: true })) return;
    const newOutline = utilDelete(outline, itemId);
    setOutline(newOutline);
    setExpandedItems(prev => { const n = new Set(prev); n.delete(itemId); return n; });
    api.put(`/api/projects/${id}`, { outline_data: { outline: newOutline } }).catch(() => {});
  };

  // ── 添加操作 ──
  const addRootOutlineItem = () => {
    const { items: newItems, newItem } = addRootItem(outline);
    setOutline(newItems);
    api.put(`/api/projects/${id}`, { outline_data: { outline: newItems } }).catch(() => {});
    setTimeout(() => {
      startEditingOutline(newItem);
      setExpandedItems(prev => new Set([...prev, newItem.uid]));
    }, 100);
  };

  const addChildOutlineItem = (parentUid: string) => {
    const { items: newItems, newItem } = addChildItem(outline, parentUid);
    setOutline(newItems);
    setExpandedItems(prev => new Set([...prev, parentUid]));
    api.put(`/api/projects/${id}`, { outline_data: { outline: newItems } }).catch(() => {});
    setTimeout(() => startEditingOutline(newItem), 100);
  };

  const addSiblingOutlineItem = (siblingUid: string) => {
    const { items: newItems, newItem } = addSiblingItem(outline, siblingUid);
    setOutline(newItems);
    api.put(`/api/projects/${id}`, { outline_data: { outline: newItems } }).catch(() => {});
    setTimeout(() => startEditingOutline(newItem), 100);
  };

  const insertSiblingOutlineItem = (targetUid: string, position: 'before' | 'after') => {
    const { items: newItems, newItem } = insertSiblingItem(outline, targetUid, position);
    setOutline(newItems);
    api.put(`/api/projects/${id}`, { outline_data: { outline: newItems } }).catch(() => {});
    setTimeout(() => startEditingOutline(newItem), 100);
  };

  const moveOutlineItem = (targetUid: string, direction: 'up' | 'down') => {
    const newOutline = moveItem(outline, targetUid, direction);
    setOutline(newOutline);
    api.put(`/api/projects/${id}`, { outline_data: { outline: newOutline } }).catch(() => {});
  };

  // ── 确认并进入 word 编辑器 ──
  const confirmOutline = async () => {
    if (outline.length === 0) {
      toast.warning('请先生成目录');
      return;
    }
    try {
      await api.put(`/api/projects/${id}`, { outline_data: { outline } });
      toast.success('目录已保存');
      navigate(`/projects/${id}/word`);
    } catch (error) {
      console.error('保存目录失败:', error);
      toast.error('保存目录失败');
    }
  };

  // ── 渲染目录项卡片 ──
  const renderOutlineItem = (item: OutlineItem, level: number = 0): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const isEditing = editingItem === item.uid;
    const isExpanded = expandedItems.has(item.uid);
    const indentClass = level > 0 ? `ml-${Math.min(level * 4, 12)}` : '';

    return (
      <div key={item.uid} className="mb-2">
        {/* 卡片 */}
        <div
          className={`border rounded-xl transition-all ${
            isEditing
              ? 'border-primary/50 bg-primary/[0.02] shadow-sm'
              : 'border-border bg-card hover:border-primary/20'
          }`}
          style={{ marginLeft: `${level * 20}px` }}
        >
          {isEditing ? (
            /* ═══════ 编辑模式 ═══════ */
            <div className="p-4 space-y-3">
              {/* 标题 */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  目录标题 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                  placeholder="输入目录标题..."
                  autoFocus
                />
              </div>

              {/* 描述 */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  目录描述
                  <span className="text-[10px] text-muted-foreground/60 ml-1">
                    （会作为AI生成内容的提示词）
                  </span>
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white resize-none"
                  placeholder="描述这个章节的内容要求和重点..."
                />
              </div>

              {/* 标签 */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">标签</label>
                <select
                  value={editTag}
                  onChange={(e) => setEditTag(e.target.value)}
                  className="text-sm border border-border rounded-lg px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {CHAPTER_TAGS.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>

              {/* 操作 */}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={saveEditOutline}
                  disabled={!editTitle.trim()}
                  className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  保存
                </button>
                <button
                  onClick={cancelEditingOutline}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg"
                >
                  取消
                </button>
              </div>
            </div>
          ) : (
            /* ═══════ 正常模式 ═══════ */
            <div>
              {/* 主行 */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {/* 展开/收起图标（有子项或描述时显示） */}
                  {(hasChildren || (item.description && item.description.trim())) && (
                    <button
                      onClick={() => toggleExpand(item.uid)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  {!hasChildren && !(item.description && item.description.trim()) && (
                    <span className="w-4 shrink-0" />
                  )}

                  {/* 编号 + 标题 */}
                  <span className={`text-sm font-medium truncate ${
                    level === 0 ? 'text-foreground' :
                    level === 1 ? 'text-emerald-700' :
                    'text-muted-foreground'
                  }`}>
                    <span className="text-muted-foreground/60 mr-1">{item.id}</span>
                    {item.title}
                  </span>

                  {/* 标签 */}
                  {item.tag && item.tag !== '默认' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
                      {item.tag}
                    </span>
                  )}
                </div>

                {/* 操作按钮组 */}
                <div className="flex items-center gap-0.5 shrink-0 ml-2">
                  {/* 前插 */}
                  <button
                    onClick={() => insertSiblingOutlineItem(item.uid, 'before')}
                    className="p-1.5 rounded-lg hover:bg-purple-50 text-purple-600 text-xs"
                    title="上方插入同级"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                    </svg>
                  </button>
                  {/* 后插 */}
                  <button
                    onClick={() => insertSiblingOutlineItem(item.uid, 'after')}
                    className="p-1.5 rounded-lg hover:bg-purple-50 text-purple-600 text-xs"
                    title="下方插入同级"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 17l-4 4m0 0l-4-4m4 4V3" />
                    </svg>
                  </button>
                  {/* 子级 */}
                  <button
                    onClick={() => addChildOutlineItem(item.uid)}
                    className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 text-xs"
                    title="添加子目录"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  {/* 分隔 */}
                  <span className="w-px h-4 bg-border mx-1" />
                  {/* 编辑 */}
                  <button
                    onClick={() => startEditingOutline(item)}
                    className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground"
                    title="编辑"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  {/* 上移 */}
                  <button
                    onClick={() => moveOutlineItem(item.uid, 'up')}
                    className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"
                    title="上移"
                  >
                    <ArrowUpIcon className="w-4 h-4" />
                  </button>
                  {/* 下移 */}
                  <button
                    onClick={() => moveOutlineItem(item.uid, 'down')}
                    className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"
                    title="下移"
                  >
                    <ArrowDownIcon className="w-4 h-4" />
                  </button>
                  {/* 删除 */}
                  <button
                    onClick={() => deleteOutlineItem(item.uid)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                    title="删除"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* 展开内容：描述 */}
              {isExpanded && item.description && item.description.trim() && (
                <div className="px-4 pb-3 pt-0 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed whitespace-pre-wrap">
                    {item.description}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 子项 */}
        {hasChildren && (
          <div>
            {item.children!.map(child => renderOutlineItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title="目录编辑"
        subtitle="编辑和调整目录结构，描述会作为AI生成内容的提示词"
        actions={
          <span className="text-xs text-muted-foreground bg-accent px-2 py-1 rounded">
            {project?.document_type === 'proposal' ? '方案' : '标书'}
          </span>
        }
      />
      <WizardLayout>
        {outline.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-lg mb-2">暂无目录</p>
            <p className="text-sm mb-4">请先在上一步生成目录，或手动添加章节</p>
            <button
              onClick={addRootOutlineItem}
              className="px-4 py-2 text-sm bg-primary text-white rounded-xl hover:bg-primary/90"
            >
              <PlusIcon className="w-5 h-5 inline-block mr-1" />
              手动添加章节
            </button>
          </div>
        ) : (
          <>
            {/* 目录卡片列表 */}
            <div className="bg-white rounded-2xl border border-border p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-foreground">目录结构</h3>
                <button
                  onClick={addRootOutlineItem}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  <PlusIcon className="w-4 h-4" /> 添加章节
                </button>
              </div>
              <div className="max-h-[55vh] overflow-y-auto pr-1">
                {outline.map((item: OutlineItem) => renderOutlineItem(item))}
              </div>
            </div>

            {/* 底部操作 */}
            <div className="flex items-center justify-between mt-5">
              <div className="flex gap-2">
                <button
                  onClick={addRootOutlineItem}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg border border-border transition-colors"
                >
                  <PlusIcon className="w-4 h-4" /> 添加根章节
                </button>
              </div>
              <button
                onClick={confirmOutline}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors shadow-sm"
              >
                确认目录，开始撰写
              </button>
            </div>
          </>
        )}

        {/* 导航按钮 */}
        <div className="flex justify-between mt-5 pt-4 border-t border-border">
          <button className="btn-secondary" onClick={() => setCurrentStep(1)}>上一步</button>
          <button className="btn-primary" onClick={confirmOutline} disabled={outline.length === 0}>
            下一步
          </button>
        </div>
      </WizardLayout>
    </div>
  );
};

export default StepOutline;
